import React from "react"
import { useTranslation } from "react-i18next"
import { AddressEntity } from "../../Objects/Person/AddressEntity"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"

interface AddressListSectionProps {
  title?: string
  emptyLabel?: string
  addresses: AddressEntity[]
  selectedAddressId?: number
  onAdd: () => void
  onClick?: (address: AddressEntity) => void
  onEdit: (address: AddressEntity) => void
  onDelete: (address: AddressEntity) => void
}

export const AddressListSection: React.FC<AddressListSectionProps> = ({
  addresses,
  onAdd,
  onEdit,
  onDelete,
  onClick,
  selectedAddressId,
  title,
  emptyLabel,
}) => {
  const { t } = useTranslation()

  const resolvedTitle = title ?? t("publicMenu:address_list.my_destinations")
  const resolvedEmpty = emptyLabel ?? t("publicMenu:address_list.empty_destinations")

  return (
    <Animate type={AnimationTypes.fadeInTraslate}>
      <div className="space-y-3 sm:space-y-4">
        <div className="flex items-center justify-between gap-2 sm:gap-3 px-1">
          <div>
            <h3 className="text-[11px] sm:text-[12px] font-black text-gray-500 uppercase tracking-[0.18em] sm:tracking-[0.2em] mb-0.5 sm:mb-1">
              {resolvedTitle}
            </h3>

            <p className="text-[8px] sm:text-[9px] text-gray-400 font-bold uppercase tracking-[0.12em] sm:tracking-[0.14em]">
              {t("publicMenu:address_list.saved_places", { count: addresses.length })}
            </p>
          </div>

          <button
            onClick={onAdd}
            className="text-[10px] sm:text-[11px] font-black text-orange-600 uppercase tracking-[0.14em] sm:tracking-[0.18em] bg-white px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg sm:rounded-xl border border-orange-200 shadow-sm hover:shadow-md hover:border-orange-300 transition-all"
            data-interaction-description-key="interactionDescriptions:publicMenu.components.addressListSection.button.onAdd"
          >
            {t("publicMenu:address_list.add")}
          </button>
        </div>

        <div className="grid grid-cols-1 gap-2">
          {addresses.length === 0 ? (
            <div className="col-span-full py-16 text-center border-2 border-dashed border-gray-200 rounded-2xl bg-white/80">
              <span className="text-4xl block mb-4 opacity-30">📍</span>

              <p className="text-[11px] font-black text-gray-300 uppercase tracking-[0.32em]">{resolvedEmpty}</p>
            </div>
          ) : (
            addresses.map((address) => {
              const isSelected = address.id === selectedAddressId

              const fullAddress = `${address.street}, ${address.number}`

              const district = `${address.neighborhood} • ${address.city} - ${address.state}`

              const cep = address.zipCode ? `CEP ${address.zipCode}` : null

              return (
                <div
                  key={address.id}
                  onClick={() => onClick?.(address)}
                  className={`bg-white rounded-2xl border overflow-hidden transition-all duration-300 cursor-pointer
                ${isSelected ? "border-orange-300 shadow-md shadow-orange-100/80" : "border-gray-200 hover:border-gray-300 hover:shadow-sm"}`}
                  data-interaction-description-key="interactionDescriptions:publicMenu.components.addressListSection.button.onClickAddress"
                >
                  <div className={`h-1 ${isSelected ? "bg-orange-500" : "bg-gray-100"}`} />

                  <div className="p-3 sm:p-4 space-y-2.5 sm:space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex items-center gap-2.5">
                        <div
                          className={`w-8 h-8 sm:w-10 sm:h-10 shrink-0 rounded-lg sm:rounded-xl flex items-center justify-center border ${
                            isSelected
                              ? "bg-orange-100 text-orange-600 border-orange-200"
                              : "bg-gray-100 text-gray-500 border-gray-200"
                          }`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2.2"
                              d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                            />
                          </svg>
                        </div>

                        <h5 className="font-black text-gray-900 text-[13px] sm:text-[15px] tracking-tight truncate">
                          {address.label}
                        </h5>
                      </div>

                      {isSelected && (
                        <span className="shrink-0 bg-orange-100 text-orange-700 border border-orange-200 text-[9px] sm:text-[10px] font-black uppercase tracking-[0.1em] sm:tracking-[0.12em] px-2 py-1 rounded-full">
                          {t("publicMenu:address_list.selected")}
                        </span>
                      )}
                    </div>

                    <div className="min-w-0 space-y-1.5">
                      <p className="text-[12px] sm:text-[13px] text-gray-700 font-semibold truncate">{fullAddress}</p>

                      <p className="text-[12px] text-gray-500 font-medium truncate">{district}</p>

                      {cep && <p className="text-[10px] text-gray-400 font-black uppercase tracking-[0.12em]">{cep}</p>}
                    </div>

                    <div className="pt-1 border-t border-gray-100 flex items-center justify-end gap-1.5 sm:gap-2">
                      <button
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          onEdit(address)
                        }}
                        className="h-7 sm:h-8 px-2.5 sm:px-3 inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.1em] sm:tracking-[0.12em] text-gray-600 hover:text-orange-600 bg-white border border-gray-200 hover:border-orange-200 hover:bg-orange-50 rounded-lg transition-all"
                        aria-label={t("publicMenu:address_list.edit")}
                        data-interaction-description-key="interactionDescriptions:publicMenu.components.addressListSection.button.edit"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2.2"
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                        {t("publicMenu:address_list.edit")}
                      </button>

                      <button
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          onDelete(address)
                        }}
                        className="h-7 sm:h-8 px-2.5 sm:px-3 inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.1em] sm:tracking-[0.12em] text-red-500 hover:text-red-600 bg-white border border-gray-200 hover:border-red-200 hover:bg-red-50 rounded-lg transition-all"
                        aria-label={t("publicMenu:address_list.delete")}
                        data-interaction-description-key="interactionDescriptions:publicMenu.components.addressListSection.button.delete"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2.6"
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7"
                          />
                        </svg>
                        {t("publicMenu:address_list.delete")}
                      </button>
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>
    </Animate>
  )
}
